import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { useAuth, useMyTeams } from '@reflex/ui/client/hooks';
import { canWriteOwned } from '@reflex/ui/client/permissions';
import { OwnerSelect, isOrganizationOwned, type OwnerSelectTeamOption } from './OwnerSelect';
import { defaultOwnerForUser, type ResourceOwnerInput } from '@reflex/shared';

interface OwnerFieldProps<T extends FieldValues> {
  /** RHF `control` from `useForm()`. */
  control: Control<T>;
  /** Field name within the form values. The field's value type must be `ResourceOwnerInput`. */
  name: Path<T>;
  /**
   * Whether this form offers org-wide ownership at all. Defaults to
   * `true`. Pass `false` on forms where the option makes no sense; to
   * gate it on the caller's permission instead, use
   * {@link OwnerFieldProps.organizationPermission}. A form on an
   * owner-scoped resource that sets neither offers every member an
   * option `assertCanWriteOwned` will reject with a 403.
   */
  allowOrganization?: boolean;
  /**
   * Permission slug required to pick the **Organization** owner, e.g.
   * `'mcp:write'`. When set, the option is offered only to callers who
   * hold it.
   *
   * This is the one place a write slug belongs on an owner-scoped create
   * form. `assertCanWriteOwned` requires the strict slug for the org tier
   * and *only* the org tier, and this field submits an explicit owner —
   * the picker starts on the caller's own user (the server's resource-kind
   * registry has no default scope). So a member who leaves the picker
   * alone gets a personal resource and succeeds; a member who picks
   * Organization gets a 403. Hiding just that option is what makes the
   * form match the server, rather than gating the whole form (which locks
   * members out of the personal create the server grants them).
   *
   * Resolved through `canWriteOwned`, NOT `usePermission`: picking
   * Organization here is an org-tier write, and org-tier writes have no
   * platform-admin bypass on the server (`hasWritePermission` goes through
   * `createPluginAuthzApi`, which pins `isSuperAdmin: false`). A
   * `usePermission` gate would offer the option to a platform admin who is
   * a plain member of the active org and then 403 on submit — while
   * `useCanWriteOwned` correctly hid Edit on that same org-owned row.
   */
  organizationPermission?: string;
  label?: string;
  description?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * React Hook Form wrapper around {@link OwnerSelect}. Hooks up the picker
 * to RHF's `Controller`, populates `currentUserId` and `teams` from
 * `useAuth()` + `useMyTeams()`, and falls back to `defaultOwnerForUser()`
 * when the form's value isn't set yet.
 *
 * Usage:
 *
 * ```tsx
 * <OwnerField control={control} name="owner" />
 * ```
 *
 * The form's `defaultValues` for that key should be either
 * `defaultOwnerForUser(user.id)` (create form) or the persisted
 * `{ ownerType, ownerUserId, ownerTeamId }` triple (edit form).
 */
export function OwnerField<T extends FieldValues>({
  control,
  name,
  allowOrganization = true,
  organizationPermission,
  label,
  description,
  className,
  disabled,
}: OwnerFieldProps<T>) {
  const auth = useAuth();
  const { user, activeOrganizationId } = auth;
  const { data: teamsData } = useMyTeams();
  const userId = user?.id ?? '';
  // "May this caller own a resource at the org tier?" is exactly what
  // `canWriteOwned` answers, so ask it rather than re-deriving the rule.
  const canOwnAsOrganization = organizationPermission
    ? canWriteOwned(activeOrganizationId, organizationPermission, auth)
    : true;
  const offerOrganization = allowOrganization && canOwnAsOrganization;

  const teams: OwnerSelectTeamOption[] = (teamsData ?? []).map((t) => {
    const team = t as { id?: string; name?: string };
    return {
      ownerTeamId: team.id ?? '',
      name: team.name ?? '',
    };
  });

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const value =
          (field.value as ResourceOwnerInput | undefined) ?? defaultOwnerForUser(userId);
        // Keep the option when the value already encodes "Organization",
        // so the picker can display the owner the resource actually has.
        // Without it the trigger renders blank: `OwnerSelect` writes only
        // through `field.onChange`, so the stored value is not at risk,
        // but a blank owner on an edit form reads as a bug. Uses the same
        // `isOrganizationOwned` predicate `OwnerSelect.encodeOwner` does,
        // so it fires exactly when that would otherwise happen.
        //
        // Narrowed to the permission gate: an explicit
        // `allowOrganization={false}` is a caller saying the option does
        // not apply to this form at all, and must still win.
        const showOrgForCurrentValue =
          allowOrganization && isOrganizationOwned(value.ownerId, activeOrganizationId);
        return (
          <OwnerSelect
            currentUserId={userId}
            teams={teams}
            currentOrganizationId={activeOrganizationId}
            allowOrganization={offerOrganization || showOrgForCurrentValue}
            value={value}
            onChange={(next) => field.onChange(next)}
            label={label}
            description={description}
            className={className}
            disabled={disabled}
          />
        );
      }}
    />
  );
}
